import CustomizeTagSelectMultiple from '@gp/components/CustomizeTagSelectMultiple';
import GeneralModal from '@gp/components/GeneralModal';
import GPSelect from '@gp/components/Select';
import GPSelectMultiple from '@gp/components/SelectMultiple';
import {
  COMMON_IMAGE_ACCEPTS,
  FIELD_COMMON_ERR_MSG,
  SELECT_PANEL_HEIGHT,
  SELECT_PANEL_HEIGHT_WITHOUT_SEARCH_BOX,
  UPLOAD_FOLDER_NAME_MAP,
  UPLOAD_BUTTON_OVERVIEW_INFO_TIPS,
} from '@gp/constants/const';
import { useAutoSaveParams } from '@gp/hooks/useAutoSaveParams';
import useCos from '@gp/hooks/useCos';
import UploadFileList from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/UpdateGRModal/UploadFileList';
import '@gp/pages/Pipeline/ProjectDetails/Milestone/components/UpdateGRModal/index.scss';
import type { IGRAction } from '@gp/pages/Pipeline/ProjectDetails/Milestone/types';
import { getBIDSTencentQCouldMassage } from '@gp/services/global';
import type { IMaterialVO } from '@gp/types/global';
import type {
  IMilestoneFinancialForecastVO,
  IMilestoneFinancialVO,
} from '@gp/types/milestones';
import {
  formatTeaFormItemMessage,
  formatTeaFormItemStatus,
} from '@gp/utils/common';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { useDeepCompareEffect, useMemoizedFn } from 'ahooks';
import classNames from 'classnames';
import { isEmpty, sortBy } from 'lodash-es';
import React, { useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useField, useForm } from 'react-final-form-hooks';
import { Col, Form, FormItem, Input, Row } from 'tea-component';
import './index.scss';

export interface IUpdateGRFormModel {
  gate_review: string;
  gate_review_decision: string;
  dev_cost_actual: string;
  budget_next_milestone: string;
  approved_budget_to_next: string;
  dev_cost: string;
  key_milestones_financial: IMilestoneFinancialVO[];
  key_milestones_financial_forecast: IMilestoneFinancialForecastVO[];
  key_feedback: string;
  action_items: IGRAction[];
  next_milestone_scope: string;
  mandate: IMaterialVO[];
}
export interface IUpdateGRModalInstance {
  init: (
    payload: Partial<IUpdateGRFormModel>,
    currentMilestoneDate?: string,
  ) => void;
}

const DEFAULT_FORM_MODEL: IUpdateGRFormModel = {
  gate_review: '',
  gate_review_decision: '',
  dev_cost_actual: '',
  budget_next_milestone: '',
  approved_budget_to_next: '',
  dev_cost: '',
  key_milestones_financial: [],
  key_milestones_financial_forecast: [],
  key_feedback: '',
  action_items: [],
  next_milestone_scope: '',
  mandate: [],
};

const ACCEPTS = [
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.csv',
  '.pdf',
  '.ppt',
  '.pptx',
  ...COMMON_IMAGE_ACCEPTS,
];

const getStudioOptionValue = (
  studioName?: string,
  studioId?: string,
): string => {
  const studioNameTrim = studioName?.trim() || '';
  const studioIdTrim = studioId?.trim() || '';
  if (!studioNameTrim || !studioIdTrim) return '';
  return `${studioNameTrim}/${studioIdTrim}`;
};

const getCommonOptions = (valStrList?: string[]): any => {
  return valStrList?.map((item: any) => {
    return {
      value: item,
      text: item,
    };
  });
};

const getCommonPlatformOptions = (valStrList?: string[]): any => {
  return valStrList?.map((item: any) => {
    return {
      value: item?.toLowerCase(),
      text: item,
    };
  });
};

const TeamGameModal = (props: any, ref: any) => {
  const {
    studioOptionList,
    visible = false,
    onConfirm,
    onCancel,
    gameInformationData,
    condDetailOptions,
    pageKeyCur,
    initialParams,
    engineOptions,
    sumbitLoading,
    engineOptional,
    setEngineOptional,
  } = props;

  const { deleteObject } = useCos({
    qCouldService: getBIDSTencentQCouldMassage,
    qCouldServiceParams: UPLOAD_FOLDER_NAME_MAP.gameInfo,
  });

  const [submitted, setSubmitted] = useState(false);
  const [initialFileDatas, setInitialFileDatas] = useState<any>([]);
  const [pTeamValue, setPTeamValue] = useState<any>('');
  const [pTeamOptions, setPTeamOptions] = useState<any>([]);
  const [studioLocationId, setStudioLocationId] = useState<any>('');
  const [isJustConfirm, setIsJustConfirm] = useState(false);
  const [isCompletedVideo, setIsCompletedVideo] = useState<boolean>(false);

  // 提交时，要删除的云文件
  const [deletePathList, setDeletePathList] = useState<any[]>([]);

  useDeepCompareEffect(() => {
    setStudioLocationId(initialParams?.studio_entity_id);
  }, [initialParams]);

  const handleConfirm = useMemoizedFn(async (values: IUpdateGRFormModel) => {
    await onConfirm?.({ ...values });
    hasChangedRef.current = false;
    setSubmitted(false);
    form?.restart();
  });

  const { form, values, submitting, handleSubmit } = useForm({
    initialValues: DEFAULT_FORM_MODEL,
    onSubmit: handleConfirm,
  });

  const initialValueRef = useRef(values);
  const hasChangedRef = useRef(false);

  useDeepCompareEffect(() => {
    if (values !== initialValueRef.current) {
      hasChangedRef.current = true;
    }

    const selectOption =
      studioOptionList?.find(
        (item: any) => item.value === values?.studio_entity_id,
      ) || {};
    if (!isEmpty(selectOption)) {
      // 在此处，如何判断values?.studio_entity_id有新变化后，更新values.studio_location的值
      if (values?.studio_entity_id !== studioLocationId && studioLocationId) {
        const newStudioLocationValue = `${selectOption?.studio_location_city}, ${selectOption?.studio_location_country}`;
        setStudioLocationId(values?.studio_entity_id);
        form?.change('studio_location', newStudioLocationValue);
      }

      const pTeamStr = selectOption?.pteam || '';
      setPTeamValue(pTeamStr);
      setPTeamOptions([
        {
          value: pTeamStr,
          text: pTeamStr,
        },
      ]);
    }
  }, [values]);

  const mandateField = useField('mandate', form);
  const studioField = useField('studio_entity_id', form);
  const ipField = useField('ip', form);
  const genreField = useField('genre', form);
  const platformField = useField('platform', form);
  const businessModelField = useField('business_model', form);
  const engineModelField = useField('engine', form);
  const publisherField = useField('publisher', form);
  const studioLocationField = useField('studio_location', form);

  useImperativeHandle(ref, () => ({
    init: (payload: any) => {
      form?.initialize({
        ...payload,
      });
      const mandate = payload?.mandate || {};

      if (!isEmpty(mandate) && Array.isArray(mandate) && mandate?.length > 0) {
        const mandateTemp = mandate[0] || {};
        if (mandateTemp?.cloud_path || mandateTemp?.path) {
          setInitialFileDatas(mandate);
        }
      } else if (
        !isEmpty(mandate) &&
        !Array.isArray(mandate) &&
        (mandate?.cloud_path || mandate?.path)
      ) {
        const fileObj = {
          path: mandate?.cloud_path || mandate?.path,
          name: mandate?.name,
          size: mandate?.file_size || mandate?.size,
        };
        setInitialFileDatas([fileObj]);
      }
      setIsJustConfirm(false);
    },
  }));

  const studio = getStudioOptionValue(
    gameInformationData?.studio,
    gameInformationData?.studio_entity_id,
  );

  const mergeStudios = (
    studios?: IGPSelectOption[] | null,
    studioId?: string,
  ): IGPSelectOption[] => {
    if ((!Array.isArray(studios) || !studios.length) && !studioId) return [];
    if (!studioId) return studios as IGPSelectOption[];
    const studio: IGPSelectOption = {
      value: studioId.split('/')[1],
      text: studioId.split('/')[0],
    };
    const studiosTmp = !Array.isArray(studios) ? [] : studios;
    const isExisted = !!studiosTmp.find((item) => item?.value === studio.value);
    const newStudio = {
      pteam: gameInformationData?.pteam || '',
      studio_location_city: gameInformationData?.studio_location_city || '',
      studio_location_country:
        gameInformationData?.studio_location_country || '',
      text: studio?.text,
      value: studio?.value,
    };

    return isExisted ? studiosTmp : sortBy([...studiosTmp, newStudio]);
  };

  const handleCancel = useMemoizedFn(() => {
    // setVisible?.(false);
    setSubmitted(false);
    hasChangedRef.current = false;
    form?.restart();
    onCancel?.(values);
  });

  const generateCustomizeAddOptionDataHandle = (
    value: string,
    text: string,
  ) => {
    setEngineOptional([
      ...engineOptional,
      { id: value || '', tag: text || '' },
    ]);
  };

  const generateCustomizeDeleteOptionDataHandle = (option: any) => {
    const newArray = engineOptional?.filter(
      (item: any) => item.id !== option?.value,
    );
    setEngineOptional(newArray);
  };

  const publishingStatsCur: any = useMemo(() => {
    if (isEmpty(values?.publisher) && isJustConfirm) {
      return 'error';
    }
    return '';
  }, [values?.publisher, isJustConfirm]);

  const studioLocationStatsCur: any = useMemo(() => {
    if (isEmpty(values?.studio_location) && isJustConfirm) {
      return 'error';
    }
    return '';
  }, [values?.studio_location, isJustConfirm]);

  const ipStatsCur: any = useMemo(() => {
    if (isEmpty(values?.ip) && isJustConfirm) {
      return 'error';
    }
    return '';
  }, [values?.ip, isJustConfirm]);

  const genreStatsCur: any = useMemo(() => {
    if (isEmpty(values?.genre) && isJustConfirm) {
      return 'error';
    }
    return '';
  }, [values?.genre, isJustConfirm]);

  const platformStatsCur: any = useMemo(() => {
    if (isEmpty(values?.platform) && isJustConfirm) {
      return 'error';
    }
    return '';
  }, [values?.platform, isJustConfirm]);

  const deleteFileFun = async () => {
    const deFunList = deletePathList?.map((path: string) => {
      return deleteObject({ path });
    });
    Promise.all(deFunList).then(() => {});
  };

  const handleSubmitInPage = useMemoizedFn(() => {
    if (
      !isEmpty(values?.publisher) &&
      !isEmpty(values?.studio_location) &&
      !isEmpty(values?.ip) &&
      !isEmpty(values?.genre) &&
      !isEmpty(values?.platform)
    ) {
      setSubmitted(true);
      handleSubmit?.();
      if (Array.isArray(deletePathList) && deletePathList?.length > 0) {
        deleteFileFun();
      }
    } else {
      setIsJustConfirm(true);
    }
  });

  // 每隔5秒自动保存
  useAutoSaveParams({
    pageKey: pageKeyCur,
    initialParams,
    saveParamsCur: values,
    isBegin: visible,
    isChangeValue: true,
  });

  return (
    <GeneralModal
      visible={visible}
      title="Game Information"
      isFont14={true}
      className={classNames('dbg-team-game-information-edit-modal', {
        'has-data': !isCompletedVideo,
      })}
      modalSize="auto"
      onConfirm={handleSubmitInPage}
      onClose={handleCancel}
      disabledConfirm={sumbitLoading}
      content={
        <div className="dbg-team-game-information-edit-modal-content font14">
          <Form
            className="gp-form dbg-team-game-information-edit-modal-content-form"
            layout="fixed"
            fixedLabelWidth="160"
          >
            <Row>
              <Col span={24}>
                <div className="dbg-team-game-information-edit-modal-content-form-des">
                  Team Information
                </div>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <FormItem
                  label="Studio"
                  required={true}
                  className="dbg-team-game-information-edit-modal-content-form-select-double"
                  // status={formatComposedTeaFormItemStatus(submitted, [studioField?.meta])}
                  // message={'formatComposedTeaFormItemMessage(submitted, [studioField?.meta])'}
                >
                  <div className="dbg-team-game-information-edit-modal-content-form-Studio">
                    <div className="dbg-team-game-information-edit-modal-content-form-Studio-left">
                      <GPSelect
                        {...studioField?.input}
                        label="Studio"
                        listHeight={SELECT_PANEL_HEIGHT}
                        options={mergeStudios(studioOptionList as any, studio)}
                        searchable
                      />
                    </div>
                    <div className="dbg-update-edit-divider">-</div>
                    <div className="dbg-team-game-information-edit-modal-content-form-Studio-right">
                      <GPSelect
                        placeholder="Please select P-team"
                        clearable={false}
                        label="P-team"
                        listHeight={SELECT_PANEL_HEIGHT_WITHOUT_SEARCH_BOX}
                        options={pTeamOptions}
                        value={pTeamValue}
                        disabled={true}
                      />
                    </div>
                  </div>
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span={12}>
                <FormItem
                  label="Studio Location"
                  className="dbg-team-game-information-edit-modal-content-form-select-item"
                  required={true}
                  status={studioLocationStatsCur}
                  message={
                    studioLocationStatsCur === 'error'
                      ? FIELD_COMMON_ERR_MSG
                      : ''
                  }
                >
                  <Input
                    className="custom-input"
                    size="full"
                    placeholder={'Please input Studio Location'}
                    {...studioLocationField?.input}
                  />
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem
                  label="Team Size"
                  className="dbg-team-game-information-edit-modal-content-form-select-item"
                >
                  <Input
                    className="custom-input"
                    size="full"
                    value={dataFormat({
                      value: gameInformationData.team_size,
                      formatRule: 'int',
                    })}
                    disabled={true}
                  />
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <FormItem
                  label="Publishing Team"
                  required={true}
                  className="dbg-team-game-information-edit-modal-content-form-select-item"
                  // message={'formatComposedTeaFormItemMessage(submitted, [studioField?.meta])'}
                  status={publishingStatsCur}
                  message={
                    publishingStatsCur === 'error' ? FIELD_COMMON_ERR_MSG : ''
                  }
                >
                  <Input
                    className="custom-input"
                    placeholder={'Please input Publishing Team'}
                    size="full"
                    {...publisherField?.input}
                  />
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <div className="dbg-information-edit-modal-content-form-des">
                  Game Information
                </div>
              </Col>
            </Row>
            <Row>
              <Col span={12}>
                <FormItem
                  label="IP"
                  required={true}
                  status={ipStatsCur}
                  message={ipStatsCur === 'error' ? FIELD_COMMON_ERR_MSG : ''}
                  className="dbg-team-game-information-edit-modal-content-form-select-item"
                >
                  <GPSelectMultiple
                    {...ipField?.input}
                    label="Ip"
                    placeholder={'Please select IP'}
                    options={getCommonOptions(condDetailOptions?.ip)}
                    className="dbg-game-overview-form-input"
                    showLabel={false}
                    showPlaceholder={true}
                    isNeedEmptyAll={false}
                  />
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem
                  label="Genre"
                  required={true}
                  status={genreStatsCur}
                  message={
                    genreStatsCur === 'error' ? FIELD_COMMON_ERR_MSG : ''
                  }
                  className="dbg-team-game-information-edit-modal-content-form-select-item"
                >
                  <GPSelectMultiple
                    {...genreField?.input}
                    label="Genre"
                    placeholder={'Please select Genre'}
                    options={getCommonOptions(condDetailOptions?.genre)}
                    className="dbg-game-overview-form-input"
                    showLabel={false}
                    showPlaceholder={true}
                    isNeedEmptyAll={false}
                  />
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span={12}>
                <FormItem
                  label="Platform"
                  required={true}
                  status={platformStatsCur}
                  message={
                    platformStatsCur === 'error' ? FIELD_COMMON_ERR_MSG : ''
                  }
                  className="dbg-team-game-information-edit-modal-content-form-select-item"
                >
                  <GPSelectMultiple
                    {...platformField?.input}
                    className="dbg-game-overview-form-input"
                    label="Platform"
                    showLabel={false}
                    showPlaceholder={true}
                    options={getCommonPlatformOptions(
                      condDetailOptions?.platform,
                    )}
                    isNeedEmptyAll={false}
                  />
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem
                  label="Business Model"
                  className="dbg-team-game-information-edit-modal-content-form-select-item"
                >
                  <GPSelectMultiple
                    {...businessModelField?.input}
                    label="Business Model"
                    placeholder={'Please select Business Model'}
                    options={getCommonOptions(
                      condDetailOptions?.bussiness_model,
                    )}
                    className="dbg-game-overview-form-input"
                    showLabel={false}
                    showPlaceholder={true}
                    isNeedEmptyAll={false}
                  />
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span={12}>
                <FormItem
                  label="Engine"
                  className="dbg-team-game-information-edit-modal-content-form-select-item"
                >
                  <CustomizeTagSelectMultiple
                    {...engineModelField?.input}
                    ignoreCase={true}
                    showPlaceholder={true}
                    options={engineOptions}
                    placeholderSelect={'Please select Engine'}
                    placeholderInput={'Please input Engine name'}
                    label={'Engine'}
                    onChange={(data?: any) => {
                      engineModelField?.input?.onChange(data || []);
                    }}
                    generateCustomizeAddOptionData={
                      generateCustomizeAddOptionDataHandle
                    }
                    generateCustomizeDeleteOptionData={
                      generateCustomizeDeleteOptionDataHandle
                    }
                  />
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <FormItem
                  label="Mandate"
                  showStatusIcon={false}
                  status={formatTeaFormItemStatus(
                    submitted,
                    mandateField?.meta,
                  )}
                  message={formatTeaFormItemMessage(
                    submitted,
                    mandateField?.meta,
                  )}
                >
                  <UploadFileList
                    uniqueId="1"
                    defaultList={initialFileDatas}
                    uploadFolderName={UPLOAD_FOLDER_NAME_MAP.gameInfo}
                    onResult={(data?: IMaterialVO[]) => {
                      mandateField?.input?.onChange(data || []);
                    }}
                    // 组件内部是否允许提交的回调
                    getIsAllow={(_payload: boolean) => {}}
                    multiple={false}
                    isSingleMultiple={true}
                    tableAutoTipHeight={120}
                    fileNameWith={330}
                    stateWith={200}
                    maxSize={500 * 1024 * 1024}
                    acceptValue={ACCEPTS}
                    limitedTypeValue={ACCEPTS.join(', ')}
                    buttonTips={UPLOAD_BUTTON_OVERVIEW_INFO_TIPS}
                    setIsCompletedVideo={setIsCompletedVideo}
                    setDeletePathList={setDeletePathList}
                    deletePathList={deletePathList}
                  />
                </FormItem>
              </Col>
            </Row>
          </Form>
        </div>
      }
    />
  );
};

export default React.forwardRef(TeamGameModal);
